import { supabase } from '@/lib/supabase/client';
import { requireAdminOrManager } from '@/lib/auth/guards';
import {
  createNotFoundError,
  createServiceError,
  fromPostgrestError,
  normalizeError,
} from '@/lib/services/errors';
import { normalizePhoneNumber } from '@/lib/utils/phone';
import type { AppUser } from '@/lib/auth';
import type { Database } from '@/lib/supabase/database.types';
import type { ShowingStatus } from '@/lib/types/showing';
import type { PostgrestResponse, PostgrestSingleResponse } from '@supabase/supabase-js';

type ShowingRow = Database['public']['Tables']['showings']['Row'];
type LeadRow = Database['public']['Tables']['leads']['Row'];
type AvailableSlotRow = { slot_time: string | null };
type ShowingListRow = { showing: ShowingRow | null; lead: LeadRow | null };
type ShowingStatusPayload = { showing: ShowingRow | null; lead: LeadRow | null };
type AdminSlotRow = {
  slot_time: string | null;
  status: string | null;
  note: string | null;
  showing_id: string | null;
};
type ShowingUnitRow = {
  id: string;
  unit_code: string;
  unit_label: string | null;
  property?: { property_code?: string | null } | null;
};

function attachAbortSignal(query: unknown, signal?: AbortSignal): void {
  if (!signal) {
    return;
  }

  const abortable = query as { abortSignal?: (signal: AbortSignal) => unknown };
  if (typeof abortable.abortSignal === 'function') {
    abortable.abortSignal(signal);
  }
}

function isCompleteStatusPayload(
  value: ShowingStatusPayload | null,
): value is { showing: ShowingRow; lead: LeadRow } {
  return value?.showing !== null && value?.lead !== null;
}

export interface BookShowingInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  unitId: string;
  date: string;
  time: string;
  message?: string;
}

export interface BookShowingResult {
  showing: ShowingRow;
  lead: LeadRow;
}

export interface ShowingWithLead {
  showing: ShowingRow;
  lead: LeadRow;
  unit?: { id: string; unitCode: string; unitLabel: string; propertyCode: string };
}

export interface ShowingSlot {
  slotTime: string;
}

export interface ShowingSlotAdmin {
  slotTime: string;
  status: 'available' | 'booked' | 'blocked';
  note: string | null;
  showingId: string | null;
}

export interface ShowingListFilter {
  status?: ShowingStatus;
  unitId?: string;
  leadId?: string;
  dateStart?: string;
  dateEnd?: string;
}

export async function fetchAvailableSlots(args: {
  unitId: string;
  date: string;
  signal?: AbortSignal;
}): Promise<string[]> {
  const { unitId, date, signal } = args;
  const query = supabase.rpc('available_showing_slots', {
    p_unit_id: unitId,
    p_date: date,
  });

  attachAbortSignal(query, signal);

  const response: PostgrestSingleResponse<AvailableSlotRow[] | null> = await query;

  if (response.error) {
    throw fromPostgrestError(response.error);
  }

  const rows: AvailableSlotRow[] = Array.isArray(response.data) ? response.data : [];

  return rows
    .map((row) => row.slot_time)
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
}

export async function bookShowing(input: BookShowingInput): Promise<BookShowingResult> {
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  const fullName = [firstName, lastName].filter((part) => part.length > 0).join(' ');
  const normalizedPhone = normalizePhoneNumber(input.phone ?? null);

  const response: PostgrestSingleResponse<ShowingStatusPayload | null> = await supabase
    .rpc('book_showing', {
      p_full_name: fullName,
      p_first_name: firstName,
      p_last_name: lastName,
      p_email: input.email,
      p_phone: normalizedPhone,
      p_unit_id: input.unitId,
      p_preferred_date: input.date,
      p_preferred_time: input.time,
      p_message: input.message ?? null,
    })
    .single();

  const { data, error } = response;

  if (error) {
    throw fromPostgrestError(error);
  }

  if (!isCompleteStatusPayload(data)) {
    throw createServiceError({ code: 'unexpected', message: 'Unable to confirm showing.' });
  }

  return { showing: data.showing, lead: data.lead };
}

export async function listShowings(args: {
  user: AppUser | null;
  filters?: ShowingListFilter;
  signal?: AbortSignal;
}): Promise<ShowingWithLead[]> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  const { filters, signal } = args;
  const listingQuery = supabase.rpc('dashboard.showing_list', {
    p_status: filters?.status ?? null,
    p_unit_id: filters?.unitId ?? null,
    p_date_start: filters?.dateStart ?? null,
    p_date_end: filters?.dateEnd ?? null,
    p_lead_id: filters?.leadId ?? null,
  });

  attachAbortSignal(listingQuery, signal);

  const response: PostgrestSingleResponse<ShowingListRow[] | null> = await listingQuery;

  const { data, error } = response;

  if (error) {
    throw fromPostgrestError(error);
  }

  const rows: ShowingListRow[] = Array.isArray(data) ? data : [];

  const showings = rows
    .filter(
      (row): row is { showing: ShowingRow; lead: LeadRow } =>
        Boolean(row.showing) && Boolean(row.lead),
    )
    .map((row) => ({
      showing: row.showing,
      lead: row.lead,
    }));

  const unitIds = Array.from(
    new Set(
      showings
        .map((entry) => entry.showing.unit_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  );

  if (unitIds.length === 0) {
    return showings;
  }

  const unitsQuery = supabase
    .from('units')
    .select('id, unit_code, unit_label, property:properties!units_property_id_fkey(property_code)')
    .in('id', unitIds);

  const unitsResponse: PostgrestResponse<ShowingUnitRow> = await unitsQuery;
  const { data: unitsData, error: unitsError } = unitsResponse;

  if (unitsError) {
    throw fromPostgrestError(unitsError);
  }

  const unitRows: ShowingUnitRow[] = Array.isArray(unitsData) ? unitsData : [];

  const unitMap = new Map<
    string,
    { id: string; unitCode: string; unitLabel: string; propertyCode: string }
  >();
  unitRows.forEach((row) => {
    const propertyCode =
      typeof row.property?.property_code === 'string' ? row.property.property_code : '';
    const unitLabel =
      typeof row.unit_label === 'string' && row.unit_label.trim().length > 0
        ? row.unit_label.trim()
        : row.unit_code;
    unitMap.set(row.id, {
      id: row.id,
      unitCode: row.unit_code,
      unitLabel,
      propertyCode,
    });
  });

  return showings.map((entry) => {
    const unitId = entry.showing.unit_id;
    const unit = typeof unitId === 'string' && unitId.length > 0 ? unitMap.get(unitId) : undefined;
    return {
      ...entry,
      unit,
    };
  });
}

export async function updateShowingStatus(args: {
  user: AppUser | null;
  showingId: string;
  status: ShowingStatus;
  scheduledAt?: string | null;
}): Promise<{ showing: ShowingRow; lead: LeadRow }> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  const response: PostgrestSingleResponse<ShowingStatusPayload | null> = await supabase
    .rpc('dashboard.showing_update_status', {
      p_showing_id: args.showingId,
      p_status: args.status,
      p_scheduled_at: args.scheduledAt ?? null,
    })
    .single();

  const { data, error } = response;

  if (error) {
    throw fromPostgrestError(error);
  }

  if (!isCompleteStatusPayload(data)) {
    throw createNotFoundError('Showing not found.');
  }

  return { showing: data.showing, lead: data.lead };
}

export async function fetchShowingLeadId(args: {
  user: AppUser | null;
  showingId: string;
  signal?: AbortSignal;
}): Promise<string> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  const normalizedShowingId = args.showingId.trim();

  if (normalizedShowingId.length === 0) {
    throw createServiceError({ code: 'bad_request', message: 'Missing showing identifier.' });
  }

  const detailQuery = supabase
    .from('showings')
    .select('lead_id')
    .eq('id', normalizedShowingId)
    .maybeSingle();

  attachAbortSignal(detailQuery, args.signal);

  const response: PostgrestSingleResponse<{ lead_id: string } | null> = await detailQuery;
  const { data, error } = response;

  if (error) {
    throw fromPostgrestError(error);
  }

  const leadId = data?.lead_id;

  if (typeof leadId !== 'string' || leadId.length === 0) {
    throw createNotFoundError('Showing not found.');
  }

  return leadId;
}

export async function fetchAdminSlots(args: {
  user: AppUser | null;
  date: string;
  signal?: AbortSignal;
}): Promise<ShowingSlotAdmin[]> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  const slotsQuery = supabase.rpc('dashboard.showing_availability_slots', {
    p_date: args.date,
  });

  attachAbortSignal(slotsQuery, args.signal);

  const response: PostgrestSingleResponse<AdminSlotRow[] | null> = await slotsQuery;

  const { data, error } = response;

  if (error) {
    throw fromPostgrestError(error);
  }

  const rows: AdminSlotRow[] = Array.isArray(data) ? data : [];

  return rows
    .map((row) => ({
      slotTime: typeof row.slot_time === 'string' ? row.slot_time : '',
      status: (row.status ?? 'blocked') as ShowingSlotAdmin['status'],
      note: row.note ?? null,
      showingId: row.showing_id ?? null,
    }))
    .filter((slot) => slot.slotTime.length > 0);
}

export async function setShowingSlot(args: {
  user: AppUser | null;
  date: string;
  time: string;
  isAvailable: boolean;
  note?: string | null;
}): Promise<void> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  const { error } = await supabase.rpc('dashboard.set_showing_availability_slot', {
    p_date: args.date,
    p_time: args.time,
    p_is_available: args.isAvailable,
    p_note: args.note ?? null,
  });

  if (error) {
    throw normalizeError(fromPostgrestError(error));
  }
}
